// ============================================================================
// describe.ts — the GRAPHICNESS axis for the demo (the third slider).
// ============================================================================
// Mirrors the product's description-graphicness control (explicit | clinical |
// tame) and the way pipeline.build_alert renders the alert body at each level:
//   - explicit / clinical: keep the keyword-derived findings line
//     (summarize_findings) PLUS the scene description prompted at that tone.
//   - tame: SKIP the findings line entirely and emit only a gentle generic
//     heads-up — so a tame prompt can't still leak "exposed breasts, with oral
//     sex" from the keyword classifier (pipeline.py build_alert `tame` branch +
//     DESIGN_FLEET §5.5 "Graphicness → description").
//
// The DEMO is precomputed and has no live VLM, so each placeholder tile carries
// THREE canned scene descriptions (explicit / clinical / tame) in demoData.ts.
// This module picks the right one for the chosen level and applies the same
// tame "drop the findings line" rule the device applies — so the prospect sees
// the real difference the slider makes, not a mock.
// ============================================================================

import type { GraphicnessLevel } from "./types";

// Plain-English part/action phrasing, ported from pipeline.py _PART_PHRASES /
// _ACTION_PHRASES (the non-technical wording a parent actually reads).
const PART_PHRASES: Record<string, string> = {
  "vagina/vulva": "exposed genitals",
  "penis/testicles": "an exposed penis",
  anus: "an exposed anus",
  buttocks: "bare buttocks",
  breasts: "exposed breasts",
  nipples: "visible nipples",
};
const ACTION_PHRASES: Record<string, string> = {
  "fellatio (blow job)": "oral sex (penis in mouth)",
  "cunnilingus (oral sex on female)": "oral sex",
  "sexual intercourse": "sexual intercourse",
  masturbation: "masturbating",
  "oral sex": "oral sex",
};

function joinHuman(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

// summarize_findings — turn structured parts/actions into a specific-but-plain
// phrase, e.g. "exposed breasts and visible nipples, with oral sex"
// (port of pipeline.py summarize_findings).
export function summarizeFindings(
  parts: string[],
  actions: string[],
  fallback = "explicit content",
): string {
  const dedup = (seq: string[]) => Array.from(new Set(seq));
  const partBits = dedup((parts ?? []).map((p) => PART_PHRASES[p] ?? p));
  const actBits = dedup((actions ?? []).map((a) => ACTION_PHRASES[a] ?? a));

  let text = joinHuman(partBits);
  if (actBits.length > 0) {
    text = (text ? `${text}, with ` : "") + joinHuman(actBits);
  }
  return text || fallback;
}

export interface DescribedAlert {
  // The keyword-derived findings line (the specific "what was seen" phrase).
  // null at the tame level — intentionally dropped so it can't leak detail.
  findingsLine: string | null;
  // The scene sentence at the chosen graphicness tone (canned per tile/level).
  sceneText: string;
}

// Build the demo's alert body for a flagged tile at the chosen graphicness.
// `scenes` are the tile's three canned descriptions; `parts`/`actions` feed the
// findings line at explicit/clinical (and are deliberately ignored at tame).
export function describeAlert(
  level: GraphicnessLevel,
  scenes: Record<GraphicnessLevel, string>,
  parts: string[],
  actions: string[],
): DescribedAlert {
  const sceneText = scenes[level];

  if (level === "tame") {
    // TAME: never derive a graphic findings line from the keyword classifier.
    // (pipeline.py build_alert tame branch.)
    return { findingsLine: null, sceneText };
  }

  const findings = summarizeFindings(parts, actions);
  const findingsLine =
    findings.charAt(0).toUpperCase() + findings.slice(1);
  return { findingsLine, sceneText };
}
